import React, { forwardRef, useCallback, useEffect, useState, useRef, useImperativeHandle } from 'react';
import Dropzone from 'react-dropzone';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { useDispatch } from 'react-redux';
import { Modal, Spinner } from 'react-bootstrap';
import { isEmpty, cloneDeep } from 'lodash';
import { useTranslation } from 'react-i18next';
import * as action from '../../Pages/DevlopmentOrder/store/DevelopmentOrder.action';
import noImage from '../../assets/images/no-image.png';
import ImportFileModal from '../../Pages/DevlopmentOrder/DesignDetail/ImportFileModal';
import { toasterMsg } from '../Toaster/Toaster';
import { isBase64Image } from '../helper';
import CustomTooltip from '../Tooltip/CustomTooltip';

const cropped = (cropSize) => {
  return cropSize === '4/3' ? 4 / 7 : 1;
};

const isInEditMode = (fromColour, image) => {
  let FP;
  if (fromColour) {
    FP = `${image.data}`;
  } else if (isBase64Image(image)) {
    FP = `${image}`;
  } else {
    FP = `${image}`;
  }
  return FP;
};

const forImage = (isEditMode, fromColour, image) => {
  let path;
  if (isEditMode) {
    path = isInEditMode(fromColour, image);
  } else path = `${image}`;
  return path;
};

const errMsgsForExtenstions = (acceptedFiles, fromDesignDetails, fromPOTemplate, setValidatedSelectedFile, t) => {
  if (isEmpty(acceptedFiles) && (fromDesignDetails || fromPOTemplate)) {
    setValidatedSelectedFile({
      extensionError: t('IMG_DROPZONE.FILE_FORMAT_NOT_VALID'),
    });
  }
  if (acceptedFiles[0] === undefined) {
    setValidatedSelectedFile({
      extensionError: t('IMG_DROPZONE.FILE_FORMAT_NOT_VALID'),
    });
  }
};

const errMsgsForSize = (acceptedFiles, files, setValidatedSelectedFile, setLoader, setFiles, t) => {
  if (acceptedFiles.length > 0) {
    const fileSize = (acceptedFiles[0].size / 1024 / 1024).toFixed(0);
    if (fileSize > 5) {
      setValidatedSelectedFile({
        sizeError: t('IMG_DROPZONE.ATTECHEMENT_FILE_MESSAGE'),
      });

      return false;
    }
    files.length === 1 && setLoader(true);
    setValidatedSelectedFile({});
    setFiles(
      acceptedFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        }),
      ),
    );
  }
};

const showErrMsgs = (validatedSelectedFile) => {
  return (
    Object.keys(validatedSelectedFile).length !== 0 && (
      <div className="d-flex justify-content-center align-items-center">
        <span className="text-danger">
          {validatedSelectedFile.sizeError}
          {validatedSelectedFile.extensionError}
        </span>
      </div>
    )
  );
};

const names = (title) => {
  return title || '';
};

const BomImageDropzone = forwardRef(
  (
    {
      onSubmit,
      image,
      isEditMode,
      acceptedFileExtenstion,
      fromColour,
      title,
      cropSize,
      fromDesignDetails,
      fromRelace,
      replaceItem,
      fromPOTemplate,
      defaultCrop,
      fromCustomModule,
    },
    ref,
  ) => {
    // Crop Img Code Start
    const imgRef = useRef(null);
    const previewCanvasRef = useRef(null);
    const dispatch = useDispatch();
    const [uploading, setUploading] = useState(false);
    const [crop, setCrop] = useState({
      unit: '%',
      width: 30,
      height: cropSize === 'FREE' && 30,
      aspect: cropSize === 'FREE' ? 0 : cropped(cropSize),
    });
    const [completedCrop, setCompletedCrop] = useState(null);
    const [validatedSelectedFile, setValidatedSelectedFile] = useState({});
    const [files, setFiles] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [submit, setSubmit] = useState(false);
    const { t } = useTranslation('common');

    useEffect(() => {
      if (replaceItem?.image) {
        let filePath = '';
        filePath = replaceItem?.image;

        // Handle S3 URLs differently
        if (filePath.includes('s3.eu-west-2.amazonaws.com')) {
          getS3ImageAsBase64(filePath)
            .then((base64String) => {
              const fileObj = base64StringtoFile(base64String, replaceItem?.name || 'image.png');
              setFiles([
                Object.assign(fileObj, {
                  preview: URL.createObjectURL(fileObj),
                }),
              ]);
            })
            .catch((error) => {
              console.error('Error loading S3 image:', error);
            });
        } else {
          toDataUrl(filePath, (file) => {
            setFiles([
              Object.assign(file, {
                preview: URL.createObjectURL(file),
              }),
            ]);
          });
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [replaceItem]);

    useEffect(() => {
      const orignalImage = async () => {
        if (fromPOTemplate) {
          const tempImage = await image;
          const cropImage = tempImage; // removed code here to handle the premade URLS   ?.includes("base64") ? tempImage : `${imageUrl}/${tempImage}`
          toDataUrl(cropImage, (file) => {
            setFiles([
              Object.assign(file, {
                preview: URL.createObjectURL(file),
              }),
            ]);
          });
        }
      };
      orignalImage();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fromPOTemplate]);

    async function getS3ImageAsBase64(s3ImageUrl) {
      const response = await fetch(s3ImageUrl, {
        method: 'GET', // *GET, POST, PUT, DELETE, etc.
        mode: 'cors', // no-cors, *cors, same-origin
        cache: 'no-cache',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      // Convert the image to a base64 string
      const blob = await response.blob();
      return await convertBlobToBase64(blob);
    }

    function convertBlobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    }

    useEffect(() => {
      if (typeof image === 'string' && image?.includes('s3.eu-west-2.amazonaws.com')) {
        getS3ImageAsBase64(image).then((base64String) => {
          setFiles([
            Object.assign(base64String, {
              preview: base64String,
            }),
          ]);
        });
      }

      if (image && image !== noImage && !fromRelace && !fromPOTemplate) {
        let filePath = '';
        filePath = forImage(isEditMode, fromColour, image);
        toDataUrl(filePath, (individualfile) => {
          setFiles([
            Object.assign(individualfile, {
              preview: URL.createObjectURL(individualfile),
            }),
          ]);
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const toDataUrl = (url, callback) => {
      const xhr = new XMLHttpRequest();
      xhr.onload = function () {
        const reader = new FileReader();
        reader.onloadend = function () {
          const fileObj = base64StringtoFile(reader.result);
          callback(fileObj);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open('GET', url);
      xhr.responseType = 'blob';
      xhr.send();
    };

    const toBase64 = async (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

    const uploadCroppedImg = async (canvas, crops) => {
      if (!crops || !canvas) {
        return;
      }
      const canvasRef = previewCanvasRef.current;
      return canvasRef.toDataURL('image/png');
    };

    const base64StringtoFile = (base64String, filename) => {
      const arr = base64String?.split(',');
      const mime = arr?.[0]?.match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    };

    useEffect(() => {
      if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
        return;
      }

      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const crop = completedCrop;
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext('2d');
      const pixelRatio = window.devicePixelRatio;

      canvas.width = crop.width * pixelRatio;
      canvas.height = crop.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
    }, [completedCrop]);

    const onFileDrop = (acceptedFiles) => {
      errMsgsForExtenstions(acceptedFiles, fromDesignDetails, fromPOTemplate, setValidatedSelectedFile, t);
      errMsgsForSize(acceptedFiles, files, setValidatedSelectedFile, setLoader, setFiles, t);
    };

    const onLoad = useCallback((img) => {
      imgRef.current = img;
      setLoader(false);
    }, []);

    const handleImgLoad = () => {
      setLoader(false);
    };

    const thumbs = files?.map((file, index) =>
      files?.length === 1 ? (
        <ReactCrop
          key={file?.size}
          style={{ verticalAlign: 'bottom' }}
          src={file?.preview}
          onImageLoaded={onLoad}
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
        />
      ) : (
        <ul key={`file-list-${index}`}>
          <li style={{ listStyleType: 'none' }} key={file.name} className="d-flex align-items-center">
            <img src={file?.preview} alt="images" width={200} height={200} onLoad={handleImgLoad} />
            <button title={t('IMG_DROPZONE.DELETE')} className="btn btn-outline" onClick={() => removeImage(file.name)}>
              <i className="icon-delete" />
            </button>
          </li>
        </ul>
      ),
    );

    const removeImage = async (name) => {
      const array = cloneDeep(files);
      const refactoredArray = array.filter((ele) => ele.name !== name);
      setFiles([...refactoredArray]);
      onFileDrop([...refactoredArray]);
    };
    const preview = files.map((file) => {
      return (
        files.length === 1 && (
          <canvas
            key={file.name}
            ref={previewCanvasRef}
            style={{
              width: Math.round(completedCrop?.width ?? 0),
              height: Math.round(completedCrop?.height ?? 0),
            }}
          />
        )
      );
    });

    useEffect(() => {
      if (files?.length > 0) {
        files?.forEach((file) => URL.revokeObjectURL(file.preview));
      }
    }, [files]);

    useEffect(() => {
      setFiles([]);
    }, []);
    useImperativeHandle(ref, () => ({
      async showCroppedImg() {
        if (!isEmpty(files) && files.length === 1) {
          if (fromDesignDetails) {
            const orgImage = [];
            const imgRes = [];
            const allFiles = [];
            const originalFiles = [];
            if (completedCrop?.width > 0) {
              const originalImg = toBase64(files[0]);
              const imageRes = await uploadCroppedImg(previewCanvasRef?.current, completedCrop);
              const fileObj = base64StringtoFile(imageRes, files[0]?.name);
              const originalFileObj = files[0];
              orgImage.push(originalImg);
              imgRes.push(imageRes);
              allFiles.push(fileObj);
              originalFiles.push(originalFileObj);
              return { imgRes, orgImage, allFiles, originalFiles };
            }
            if (files.length > 0) {
              const originalImg = await toBase64(files[0]);
              const imageRes = await toBase64(files[0]);
              const fileObj = files[0];
              const originalFileObj = files[0];
              orgImage.push(originalImg);
              imgRes.push(imageRes);
              allFiles.push(fileObj);
              originalFiles.push(originalFileObj);
              return { imgRes, orgImage, allFiles, originalFiles };
            }
            return '';
          }
          if (fromPOTemplate) {
            if (!isEmpty(files) && files?.length === 1) {
              const orgImage = [];
              const imgRes = [];
              const allFiles = [];
              const originalFiles = [];

              for (const ele of files) {
                const originalImg = await toBase64(ele);
                const imageRes = await toBase64(ele);
                const fileObj = ele;
                const originalFileObj = ele;
                orgImage.push(originalImg);
                imgRes.push(imageRes);
                allFiles.push(fileObj);
                originalFiles.push(originalFileObj);
              }
              return { imgRes, orgImage, allFiles, originalFiles };
            }
            return '';
          }
          if (completedCrop?.width > 0) {
            const originalImg = await toBase64(files[0]);
            const imageRes = await uploadCroppedImg(previewCanvasRef.current, completedCrop);
            const fileObj = base64StringtoFile(imageRes, files[0].name);
            const originalFileObj = files[0];
            return {
              imageRes,
              originalImg,
              fileObj,
              originalFileObj,
            };
          }
          if (files.length > 0) {
            const originalImg = await toBase64(files[0]);
            const imageRes = await toBase64(files[0]).catch((e) => console.log(e, 'errr'));
            const fileObj = files[0];
            const originalFileObj = files[0];
            return { imageRes, originalImg, fileObj, originalFileObj };
          }
          return '';
        }
        if (!isEmpty(files)) {
          const orgImage = [];
          const imgRes = [];
          const allFiles = [];
          const originalFiles = [];
          for (const ele of files) {
            const originalImg = await toBase64(ele);
            const imageRes = await toBase64(ele).catch((e) => console.log(e, 'errr'));
            const fileObj = ele;
            const originalFileObj = ele;
            orgImage.push(originalImg);
            imgRes.push(imageRes);
            allFiles.push(fileObj);
            originalFiles.push(originalFileObj);
          }
          return { imgRes, orgImage, allFiles, originalFiles };
        }
        return '';
      },
    }));

    const openImportModal = () => {
      setShowImportModal(true);
    };

    const hideImportingFile = async () => {
      setShowImportModal(false);
      await dispatch(action.clearDocOptions());
      await dispatch(action.resetModal());
    };

    const images = (selection) => {
      return selection?.imageUrl ? selection?.imageUrl : '';
    };

    const pathSection = (selection) => {
      return selection?.path ? selection?.path : images(selection);
    };

    const selectedFile = async (selection) => {
      let filePath = `${selection?.pdf ? selection.pdf : pathSection(selection)}`;
      if (selection?.docType === 'Colour') {
        filePath = filePath?.replace('static', '');
      }
      toDataUrl(filePath, (singlefile) => {
        setFiles([
          Object.assign(singlefile, {
            preview: URL.createObjectURL(singlefile),
          }),
        ]);
      });
      hideImportingFile();
      await dispatch(action.clearDocOptions());
      await dispatch(action.resetModal());
    };

    const imgup = () => {
      onSubmit();
      setFiles([]);
      setSubmit(true);
    };

    const uploadImage = async () => {
      if (imgRef.current === null && fromCustomModule) {
        toasterMsg(t('IMG_DROPZONE.PLEASE_UPLOAD_FILE'), 'danger');
        return false;
      }
      setUploading(true);
      await onSubmit();
      setUploading(false);
    };
    const toRenderDesignModal = () => {
      return fromDesignDetails ? (
        <Modal show={fromDesignDetails} className="common-custom-modal modal-large upload-doc-modal" aria-labelledby="contained-modal-title-vcenter">
          <Modal.Header className="modal-header-custom">
            <Modal.Title>
              <span className="f-24 text-bold">{t('IMG_DROPZONE.UPLOAD_IMAGE')}</span>
            </Modal.Title>
            <div className="ml-auto design-header">
              {fromDesignDetails && !defaultCrop ? (
                <CustomTooltip tooltipText={t('COMMON_LABEL.IMPORT_FILES')}>
                  <button name="importFiles" onClick={openImportModal} className="btn p-0" type="button">
                    <span className="icon-Upload-1 text-grey-color f-28" />
                  </button>
                </CustomTooltip>
              ) : null}

              <button name="close" onClick={() => onSubmit('close')} className="btn p-0" type="button">
                <span className="icon-close f-20 text-grey-color  cursor-pointer text-dark text-decoration-none" />
              </button>
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="common-image-upload">
              {fromDesignDetails && !defaultCrop ? (
                <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <section className="upload-image-section" {...getRootProps()}>
                      <div className="w-100 text-center">
                        <input {...getInputProps()} />
                        {isDragActive ? (
                          <p className="mb-0"> {t('IMG_DROPZONE.DROP_FILE_HERE')}</p>
                        ) : (
                          <p className="mb-0">
                            {t('IMG_DROPZONE.DRAG_DROP')} {names(title)} {title?.includes('image') ? '' : 'image'} {t('IMG_DROPZONE.JPEG_FILE_MESSAGE')}
                          </p>
                        )}
                      </div>

                      <label className="upload-icon ">
                        <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                        <a href={() => false} className="mt-16 align-items-center border-0 p-0 justify-content-center">
                          <span className="icon icon-Upload-1" />
                        </a>
                      </label>
                    </section>
                  )}
                </Dropzone>
              ) : null}
              {submit && imgRef.current === null && (
                <div className="d-flex justify-content-center align-items-center">
                  <span className="text-danger">{t('IMG_DROPZONE.PLEASE_SELECT_FILE')}</span>
                </div>
              )}
              {showErrMsgs(validatedSelectedFile)}
              <div className="upload-crop-section upload-crop-doc">
                <div className="crop-box">
                  <label className="title">
                    {' '}
                    {fromDesignDetails && defaultCrop ? t('IMG_DROPZONE.CROP_DEFALT_IMAGE') : t('IMG_DROPZONE.UPLOAD_IMAGE_PREVIEW')}
                  </label>
                  <div className="image-view-box">
                    {loader && (
                      <div className="w-100 bg-dark text-center">
                        <span className="text-light">{t('IMG_DROPZONE.LOADINGS')}</span>
                      </div>
                    )}
                    <div className="d-flex flex-wrap justify-content-center">{thumbs}</div>
                    <div style={{ display: 'none' }}>{preview}</div>
                  </div>
                </div>
              </div>

              <div className="common-model-footer mt-16">
                <button onClick={imgup} type="button" className="btn p-0" disabled={Object.values(validatedSelectedFile).length}>
                  <i className="icon-Tick-1 f-24" />
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      ) : (
        <div className="common-image-upload">
          <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
            {({ getRootProps, getInputProps, isDragActive }) => (
              <section className="upload-image-section" {...getRootProps()}>
                <div className="w-100 text-center">
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <p className="mb-0">{t('IMG_DROPZONE.DROP_FILES')}</p>
                  ) : (
                    <p className="mb-0">
                      {t('IMG_DROPZONE.DRAG_DROP')} {names(title)} {title?.includes('image') ? '' : 'image'} {t('IMG_DROPZONE.JPEG_FILE_MESSAGE')}
                    </p>
                  )}
                </div>

                <label className="upload-icon">
                  <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                  <a href={() => false} className="mt-16 align-items-center justify-content-center">
                    <i className="icon icon-Upload-1" />
                  </a>
                </label>
              </section>
            )}
          </Dropzone>

          {showErrMsgs(validatedSelectedFile)}

          <div className="upload-crop-section upload-crop-doc">
            <div className="crop-box">
              <label className="title">{t('IMG_DROPZONE.UPLOAD_IMAGE_PREVIEW')}</label>
              <div className="image-view-box">
                {loader && (
                  <div className="w-100 bg-dark text-center">
                    <span className="text-light">{t('IMG_DROPZONE.LOADINGS')}</span>
                  </div>
                )}
                <div className="d-flex justify-content-center">
                  <div className="col-md-5 p-0">{thumbs}</div>
                </div>
                <div style={{ display: 'none' }}>{preview}</div>
              </div>
            </div>
          </div>

          <div className="common-model-footer mt-16">
            <button onClick={uploadImage} type="button" className="btn p-0" disabled={Object.values(validatedSelectedFile).length}>
              {uploading ? <Spinner animation="border" /> : <i className="icon-Tick-1 f-24" />}
            </button>
          </div>
        </div>
      );
    };

    return (
      <>
        {fromPOTemplate ? (
          <Modal show={fromPOTemplate} className="common-custom-modal modal-large" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header className="d-flex">
              <span className="mt-2 f-14">{t('IMG_DROPZONE.UPLOAD_IMAGE')}</span>
              <button name="close" onClick={() => onSubmit('close')} className="btn" type="button">
                <span className="icon-close cursor-pointer text-dark text-decoration-none" />
              </button>
            </Modal.Header>
            <Modal.Body>
              <div className="common-image-upload">
                <Dropzone accept={acceptedFileExtenstion} onDrop={onFileDrop}>
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <section className="upload-image-section" {...getRootProps()}>
                      <div className="w-100 py-5 text-center">
                        <input {...getInputProps()} />
                        {isDragActive ? <p className="mb-0">{t('IMG_DROPZONE.DROP_FILE_HERE')}</p> : <p className="mb-0 ">{t('IMG_DROPZONE.FILE_DROP')}</p>}
                      </div>

                      <label className="upload-icon ">
                        <input {...getInputProps()} type="file" accept={acceptedFileExtenstion} className="img-Upload" />
                        <a href={() => false} className="align-items-center justify-content-center">
                          <span className="icon-upload" />
                        </a>
                      </label>
                    </section>
                  )}
                </Dropzone>
                {submit && imgRef.current === null && (
                  <div className="d-flex justify-content-center align-items-center">
                    <span className="text-danger">{t('IMG_DROPZONE.PLEASE_SELECT_FILES')}</span>
                  </div>
                )}
                {showErrMsgs(validatedSelectedFile)}
                <div className="upload-crop-section mt-3">
                  <div className="crop-box">
                    <label className="title">{t('IMG_DROPZONE.UPLOAD_IMAGE_CROP')}</label>
                    <div className="image-view-box">
                      {loader && (
                        <div className="w-100 bg-dark text-center">
                          <span className="text-light">{t('IMG_DROPZONE.LOADING')}</span>
                        </div>
                      )}
                      <div className="d-flex justify-content-center">{thumbs}</div>
                      <div style={{ display: 'none' }}>{preview}</div>
                    </div>
                  </div>
                </div>

                <div className="modal-buttons  d-flex justify-content-end">
                  <button onClick={imgup} type="button" className="btn support-add" disabled={Object.values(validatedSelectedFile)?.length}>
                    {t('IMG_DROPZONE.UPLOAD_IMAGES')}
                  </button>
                </div>
              </div>
            </Modal.Body>
          </Modal>
        ) : (
          toRenderDesignModal()
        )}
        {showImportModal && <ImportFileModal onOpen={showImportModal} toggle={hideImportingFile} onSubmit={selectedFile} />}
      </>
    );
  },
);

export default BomImageDropzone;
